import {
  ChangeDetectionStrategy,
  Component, computed,
  DestroyRef,
  inject, OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FeedFacade } from '@core/facades/feed.facade';
import { ComponentHelperService, PlatformService } from '@core/services';
import { MediaSwiperComponent } from '@shared/components/media-swiper/media-swiper.component';
import { ResponsiveLayoutComponent } from '@shared/layouts';
import { HeaderComponent, LoaderComponent } from '@shared/components';
import { FeedItem } from '@core/models';
import { BreakpointFacade } from '@core/facades';
import { filter } from 'rxjs/operators';
import { MediaListComponent } from '@modules/media/media-list/media-list.component';

@Component({
  standalone: true,
  selector: 'cheelee-feed',
  templateUrl: './feed.component.html',
  styleUrl: './feed.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ResponsiveLayoutComponent,
    MediaSwiperComponent,
    LoaderComponent,
    HeaderComponent,
    MediaListComponent,
  ],
})
export class FeedComponent implements OnInit, OnDestroy {
  private readonly breakpointFacade = inject(BreakpointFacade);

  private readonly destroyRef = inject(DestroyRef);

  private readonly feedFacade = inject(FeedFacade);

  private readonly platformService = inject(PlatformService);

  protected readonly isDesktop = this.breakpointFacade.isDesktop;

  protected readonly isMobile = this.breakpointFacade.isMobile;

  protected readonly feed = signal<FeedItem[]>([]);

  protected readonly feed$ = this.feedFacade.feed$;

  protected readonly headerHeight = inject(ComponentHelperService).headerHeight;

  protected readonly isFeedLoading = computed(() => {
    const isLoading = this.feedFacade.feedIsLoading;
    const feedLength = this.feed().length;
    return isLoading() && feedLength < 1;
  });

  ngOnInit(): void {
    this.feedFacade.playMedia();
    this.feed$
      .pipe(filter(feed => feed?.length > 0))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((feed) => this.feed.set(feed));

    if (this.platformService.isBrowser()) {
      this.feedFacade.loadFeedMedia();
    }
  }

  ngOnDestroy(): void {
    this.feedFacade.clear();
  }
}
